import { useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { Eye, MoreHorizontal, Pause, Play, X } from 'lucide-react'
import { useState } from 'react'
import { EnhancedTable } from '@/shared/components/table/EnhancedTable'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { ConfirmationDialog } from '@/shared/components/ui/confirmation-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import {
  type ContractResponse,
  contractService,
} from '../../services/contractService'

type Contract = ContractResponse

interface ContractListTableProps {
  contracts: Contract[]
  loading: boolean
  onContractAction?: (
    contractId: string,
    action: 'activate' | 'pause' | 'cancel' | 'resume'
  ) => void
}

export function ContractListTable({
  contracts,
  loading,
  onContractAction,
}: ContractListTableProps) {
  const navigate = useNavigate()
  const [pauseConfirmContract, setPauseConfirmContract] =
    useState<Contract | null>(null)
  const [cancelConfirmContract, setCancelConfirmContract] =
    useState<Contract | null>(null)
  const [resumeConfirmContract, setResumeConfirmContract] =
    useState<Contract | null>(null)

  const getStatusVariant = (status: Contract['status']) => {
    switch (status) {
      case 'active':
        return 'default'
      case 'draft':
        return 'secondary'
      case 'suspended':
        return 'secondary'
      case 'canceled' as Contract['status']:
        return 'destructive'
      case 'expired':
        return 'outline'
      default:
        return 'outline'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const _formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount)
  }

  // Create skeleton data for loading state
  const skeletonContracts: Contract[] = loading
    ? Array.from(
        { length: 3 },
        (_, i) =>
          ({
            id: `skeleton-${i}`,
            tenant_id: '',
            environment_id: '',
            plan_id: '',
            customer_id: '',
            organization_id: null,
            billing_entity_id: null,
            contract_id: '',
            name: '',
            description: null,
            status: 'draft' as const,
            start_date: '',
            end_date: null,
            billing_interval: 'monthly' as const,
            currency: 'USD',
            collection_method: '',
            payment_terms: 30,
            trial_start: null,
            trial_end: null,
            signed_at: null,
            activated_at: null,
            canceled_at: null,
            plan: {
              id: '',
              name: '',
              description: null,
              type: '',
              status: '',
            },
            customer: {
              id: '',
              name: '',
              email: null,
              status: '',
            },
            metadata: {},
            created_at: '',
            updated_at: '',
          }) as unknown as Contract
      )
    : []

  const displayData = loading ? skeletonContracts : contracts

  const columns: ColumnDef<Contract>[] = [
    {
      accessorKey: 'name',
      header: 'Contract Name',
      cell: ({ row }) =>
        loading ? (
          <div className='h-4 bg-slate-300 dark:bg-slate-600 rounded animate-pulse w-32'></div>
        ) : (
          <div className='font-medium'>{row.original.name}</div>
        ),
      size: 250,
    },
    {
      accessorKey: 'plan.name',
      header: 'Plan',
      cell: ({ row }) =>
        loading ? (
          <div className='h-4 bg-slate-300 dark:bg-slate-600 rounded animate-pulse w-24'></div>
        ) : (
          <div className='text-sm'>
            <div className='font-medium'>{row.original.plan.name}</div>
            {row.original.plan.description && (
              <div className='text-muted-foreground text-xs'>
                {row.original.plan.description}
              </div>
            )}
          </div>
        ),
    },
    {
      accessorKey: 'billing_interval',
      header: 'Billing',
      cell: ({ row }) =>
        loading ? (
          <div className='h-4 bg-slate-300 dark:bg-slate-600 rounded animate-pulse w-20'></div>
        ) : (
          <div className='text-sm'>
            <div className='capitalize'>{row.original.billing_interval}</div>
            <div className='text-muted-foreground text-xs'>
              {row.original.currency}
            </div>
          </div>
        ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) =>
        loading ? (
          <div className='h-6 bg-slate-300 dark:bg-slate-600 rounded animate-pulse w-16'></div>
        ) : (
          <Badge variant={getStatusVariant(row.original.status)}>
            {row.original.status.charAt(0).toUpperCase() +
              row.original.status.slice(1)}
          </Badge>
        ),
    },
    {
      accessorKey: 'start_date',
      header: 'Start Date',
      cell: ({ row }) =>
        loading ? (
          <div className='h-4 bg-slate-300 dark:bg-slate-600 rounded animate-pulse w-24'></div>
        ) : (
          <div className='text-sm'>{formatDate(row.original.start_date)}</div>
        ),
    },
    {
      accessorKey: 'end_date',
      header: 'End Date',
      cell: ({ row }) =>
        loading ? (
          <div className='h-4 bg-slate-300 dark:bg-slate-600 rounded animate-pulse w-24'></div>
        ) : (
          <div className='text-sm'>
            {row.original.end_date ? (
              formatDate(row.original.end_date)
            ) : (
              <span className='text-muted-foreground'>Forever</span>
            )}
          </div>
        ),
    },
    {
      accessorKey: 'created_at',
      header: 'Created',
      cell: ({ row }) =>
        loading ? (
          <div className='h-4 bg-slate-300 dark:bg-slate-600 rounded animate-pulse w-20'></div>
        ) : (
          <div className='text-sm text-muted-foreground'>
            {formatDate(row.original.created_at)}
          </div>
        ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const contract = row.original
        return loading ? (
          <div className='h-4 bg-slate-300 dark:bg-slate-600 rounded animate-pulse w-6'></div>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' className='h-8 w-8 p-0'>
                <span className='sr-only'>Open menu</span>
                <MoreHorizontal className='h-4 w-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem onClick={() => handleRowClick(contract)}>
                <Eye className='mr-2 h-4 w-4' />
                View Details
              </DropdownMenuItem>

              {contract.status === 'draft' && (
                <DropdownMenuItem
                  onClick={() => onContractAction?.(contract.id, 'activate')}
                >
                  <Play className='mr-2 h-4 w-4' />
                  Activate Contract
                </DropdownMenuItem>
              )}

              {contract.status === 'active' && (
                <>
                  <DropdownMenuItem
                    onClick={() => setPauseConfirmContract(contract)}
                  >
                    <Pause className='mr-2 h-4 w-4' />
                    Pause Contract
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setCancelConfirmContract(contract)}
                  >
                    <X className='mr-2 h-4 w-4' />
                    Cancel Contract
                  </DropdownMenuItem>
                </>
              )}

              {contract.status === 'suspended' && (
                <DropdownMenuItem
                  onClick={() => setResumeConfirmContract(contract)}
                >
                  <Play className='mr-2 h-4 w-4' />
                  Resume Contract
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
      size: 50,
    },
  ]

  const handlePauseConfirm = async () => {
    if (pauseConfirmContract) {
      try {
        await contractService.pauseContract(pauseConfirmContract.id)
        onContractAction?.(pauseConfirmContract.id, 'pause')
        setPauseConfirmContract(null)
      } catch (error) {
        toast.error(extractErrorMessage(error))
      }
    }
  }

  const handleCancelConfirm = async () => {
    if (cancelConfirmContract) {
      try {
        await contractService.cancelContract(cancelConfirmContract.id)
        onContractAction?.(cancelConfirmContract.id, 'cancel')
        setCancelConfirmContract(null)
      } catch (error) {
        toast.error(extractErrorMessage(error))
      }
    }
  }

  const handleResumeConfirm = async () => {
    if (resumeConfirmContract) {
      try {
        await contractService.activateContract(resumeConfirmContract.id)
        onContractAction?.(resumeConfirmContract.id, 'resume')
        setResumeConfirmContract(null)
      } catch (error) {
        toast.error(extractErrorMessage(error))
      }
    }
  }

  const handleRowClick = (contract: Contract) => {
    navigate({
      to: `/customers/${contract.customer_id}/contracts/${contract.id}`,
    })
  }

  return (
    <div className='space-y-4'>
      {/* Enhanced Contracts Table */}
      <div className='[&_table]:bg-transparent [&_thead]:bg-transparent [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50 [&_tbody_tr]:cursor-pointer'>
        <EnhancedTable
          data={displayData}
          columns={columns}
          height='400px'
          className='bg-transparent border-0'
          onRowClick={loading ? undefined : handleRowClick}
        />
      </div>

      {/* Pause Confirmation Dialog */}
      <ConfirmationDialog
        open={!!pauseConfirmContract}
        onOpenChange={() => setPauseConfirmContract(null)}
        title='Pause Contract'
        description={`Are you sure you want to pause the contract "${pauseConfirmContract?.name}"? The contract will be suspended and billing will be paused until resumed.`}
        confirmText='Pause Contract'
        onConfirm={handlePauseConfirm}
      />

      {/* Cancel Confirmation Dialog */}
      <ConfirmationDialog
        open={!!cancelConfirmContract}
        onOpenChange={() => setCancelConfirmContract(null)}
        title='Cancel Contract'
        description={`Are you sure you want to cancel the contract "${cancelConfirmContract?.name}"? This action cannot be undone and will permanently cancel the contract.`}
        confirmText='Cancel Contract'
        onConfirm={handleCancelConfirm}
      />

      {/* Resume Confirmation Dialog */}
      <ConfirmationDialog
        open={!!resumeConfirmContract}
        onOpenChange={() => setResumeConfirmContract(null)}
        title='Resume Contract'
        description={`Are you sure you want to resume the contract "${resumeConfirmContract?.name}"? Billing will resume according to the contract terms.`}
        confirmText='Resume Contract'
        onConfirm={handleResumeConfirm}
      />
    </div>
  )
}
